import type { Persona, Rule } from "../rules/types";
import type { AuditFinding, AuditRule, UserAuditRule } from "../rules/audit/types";
import type { SheetRegions } from "../regions/types";
import type { FormulaRegion } from "../formulas/types";
import type { CellTraceData } from "../trace/types";
import { getTraceForActiveCell } from "../trace/scanner";
import type { StringCell } from "../strings/types";
import type { CellTypeReport, CellTypesConfig } from "../celltypes/types";
import type { FunctionCatalogEntry } from "../celltypes/catalog";
import type { UncataloguedFunction } from "../celltypes/uncatalogued";
import { scanCellTypes } from "../celltypes/scanner";
import {
  saveWeights,
  saveDirectRules,
  saveCatalogEntries,
  resetCatalog,
} from "../celltypes/storage";
import { scanAllWorksheets, classifyAllRegions } from "../regions/scanner";
import { RULE_DESCRIPTORS } from "../regions/classify/rules";
import { loadOverrides, saveOverrides } from "../regions/classify/storage";
import type {
  ClassificationRuleDescriptor,
  ClassificationRuleOverride,
} from "../regions/classify/types";
import { scanFormulaRegions } from "../formulas/scanner";
import {
  loadKindRules as loadFormulaKindRules,
  saveKindRules as saveFormulaKindRules,
  resetKindRules as resetFormulaKindRules,
  type FormulaKindRule,
} from "../formulas/kindRules";
import {
  addPersona,
  addUserRule,
  deletePersona,
  loadPersonas,
  updatePersona,
  updateUserRule,
} from "../rules/storage";
import { loadAllRules } from "../rules/registry";
import { loadAllAuditRules } from "../rules/audit/registry";
import { addUserAuditRule, updateUserAuditRule } from "../rules/audit/storage";
// Re-export the rule editor / persona manager helpers so callers that still
// import them from dialog-host (e.g. the Reformat overlay UI) work unchanged.
export { openRuleEditor, openPersonaManager } from "../rules/dialogs";
import { getToken } from "../lib/auth";
import { emitEvent } from "../lib/telemetry";
import { reportError } from "../lib/errorLog";
import type { NamedRangeInfo } from "../namedRanges/types";
import { scanNamedRanges } from "../namedRanges/scanner";

export interface PageText {
  page: number;
  lines: string[];
}

export interface TableData {
  page: number;
  index: number;
  rows: Array<Array<string | null>>;
}

export interface ExtractionResult {
  filename: string;
  sha256: string;
  pages: number;
  text: PageText[];
  tables: TableData[];
  ocr_text: PageText[];
}


// ============================================================================
// Review dialog (audit findings)
// ============================================================================

interface NavigateFromReviewDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface CloseFromReviewDialog {
  type: "close";
}

type ReviewFromDialog = NavigateFromReviewDialog | CloseFromReviewDialog | ReadyFromDialog;

interface InitToReview {
  type: "init";
  findings: AuditFinding[];
  scope: string;
}

/**
 * Open the review-findings dialog. Stays open until the user closes it
 * (Done button or window X). Each click on a finding inside the dialog
 * triggers navigation to that cell in Excel.
 */
export function openReviewResults(findings: AuditFinding[], scope: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/review.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 75, height: 80, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          try {
            const event = arg as { message: string };
            const msg = JSON.parse(event.message) as ReviewFromDialog;
            if (msg.type === "ready") {
              const init: InitToReview = { type: "init", findings, scope };
              dialog.messageChild(JSON.stringify(init));
              return;
            }
            if (msg.type === "navigate") {
              // Fire-and-forget — dialog stays open while user reviews cells.
              void navigateToCell(msg.sheetName, msg.address);
              return;
            }
            if (msg.type === "close") {
              finish();
              return;
            }
          } catch (err) {
            console.error("review dialog message failed:", err);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

/**
 * Write the current formula-kind rules to a new worksheet. Each rule becomes
 * a row in a tabular layout (Name / Pattern / Flags / Kind / Enabled) plus a
 * "TS source" column whose cells contain a TypeScript object literal ready
 * to paste into the `DEFAULT_RULES` array in `src/formulas/kindRules.ts`.
 *
 * The new sheet is named "Formula kind rules" with a timestamp suffix on
 * the second-or-later export so prior exports aren't overwritten.
 */
async function exportKindRulesToWorksheet(rules: FormulaKindRule[]): Promise<void> {
  // Build the value matrix BEFORE entering Excel.run so any data-shaping bug
  // surfaces as a normal JS error rather than as an Excel "internal error".
  const headers = ["Name", "Pattern", "Flags", "Kind", "Shapes", "Enabled", "TS source"];
  const cellRows: string[][] = [headers];
  for (const r of rules) {
    cellRows.push([
      safeString(r.name),
      safeString(r.pattern),
      safeString(r.flags),
      safeString(r.kind),
      safeString(Array.isArray(r.shapes) ? r.shapes.join(",") : ""),
      r.enabled ? "TRUE" : "FALSE",
      safeString(ruleToTsLiteral(r)),
    ]);
  }

  const width = headers.length;
  for (const row of cellRows) {
    while (row.length < width) row.push("");
  }

  console.log(
    "[pane] export: prepared %d rows x %d cols for %d rules",
    cellRows.length,
    width,
    rules.length
  );
  // Dump each row so if Excel rejects the write we can see which value is the
  // suspect (e.g. a stray binary char, an over-long literal, etc.).
  for (let i = 0; i < cellRows.length; i++) {
    console.log("[pane] export row", i, cellRows[i]);
  }

  // Per-row writes with a sync after EACH row. Slower, but if Excel rejects a
  // specific value we know exactly which row killed it instead of getting a
  // generic GeneralException for the whole batch.
  const job = Excel.run(async (context) => {
    const sheetName = uniqueSheetName("KindRules");
    console.log("[pane] export: adding worksheet:", sheetName);
    const ws = context.workbook.worksheets.add(sheetName);
    await context.sync();

    for (let i = 0; i < cellRows.length; i++) {
      const row = cellRows[i];
      const rowRange = ws.getRangeByIndexes(i, 0, 1, width);
      rowRange.values = [row];
      try {
        // Per-row sync is intentional here: it pinpoints which row Excel
        // rejects when GeneralException fires on a bulk write.
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync();
      } catch (e) {
        console.error("[pane] export: row %d failed:", i, row, e);
        throw new Error(
          `Row ${i} rejected by Excel: ${formatExcelError(e)} — values: ${JSON.stringify(row)}`
        );
      }
    }
    console.log("[pane] export: all rows written");
  });

  // Watchdog: if Excel.run hangs (it has done so when the pane is "busy"
  // mid-message-handler), at least surface a real error to the dialog instead
  // of leaving it stuck on "Exporting…".
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error("Export timed out after 30s (Excel.run never resolved)"));
    }, 30000);
  });

  try {
    await Promise.race([job, timeout]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/**
 * Best-effort error message extractor for Office.js errors, which often have
 * a useful `debugInfo.message` or `code` field that the bare Error message
 * doesn't expose.
 */
function formatExcelError(err: unknown): string {
  if (err instanceof Error) {
    const anyErr = err as unknown as {
      debugInfo?: { message?: string; code?: string; errorLocation?: string };
      code?: string;
    };
    const parts: string[] = [];
    if (err.message) parts.push(err.message);
    if (anyErr.debugInfo?.code && anyErr.debugInfo.code !== err.message) {
      parts.push(`(${anyErr.debugInfo.code})`);
    }
    if (anyErr.debugInfo?.message && anyErr.debugInfo.message !== err.message) {
      parts.push(`— ${anyErr.debugInfo.message}`);
    }
    return parts.join(" ");
  }
  return String(err);
}

function safeString(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = String(v);
  // Strip control characters (Excel rejects most of them with a generic
  // "GeneralException" on the values write).
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, "");
  // Excel treats leading `=`, `+`, `-`, `@` as formula starters and may either
  // try to evaluate the string or reject the whole 2D write. Prefix a literal
  // apostrophe to force text-mode entry.
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  // Hard-cap at Excel's per-cell 32767-char limit, leaving headroom.
  if (s.length > 32000) s = s.slice(0, 32000) + "…";
  return s;
}

/**
 * Build a sheet name that fits Excel's 31-char limit AND is unlikely to
 * collide with an existing sheet (timestamp to seconds). Excel forbids
 * the characters \ / ? * [ ] : in sheet names — none of our inputs use any.
 */
function uniqueSheetName(base: string): string {
  // HH-MM-SS gives seconds-level uniqueness without needing an async lookup
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  const stamp = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  let name = `${base} ${stamp}`;
  if (name.length > 31) name = name.slice(0, 31);
  return name;
}

/**
 * Render one rule as a TypeScript object literal, one line. Escapes
 * backslashes and quotes inside string values so the output is paste-safe
 * into a `.ts` file.
 */
function ruleToTsLiteral(rule: FormulaKindRule): string {
  const esc = (s: string): string => s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const shapesArr = Array.isArray(rule.shapes) ? rule.shapes : [];
  const shapesLiteral = shapesArr.map((s) => `"${s}"`).join(", ");
  return (
    `{ id: "${esc(rule.id)}", ` +
    `name: "${esc(rule.name)}", ` +
    `pattern: "${esc(rule.pattern)}", ` +
    `flags: "${esc(rule.flags)}", ` +
    `kind: "${esc(rule.kind)}", ` +
    `shapes: [${shapesLiteral}], ` +
    `enabled: ${rule.enabled} },`
  );
}

async function navigateToCell(sheetName: string, address: string): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
      await ctx.sync();
      // isNullObject is auto-populated by sync for *OrNullObject methods;
      // the lint rule mis-flags it as a navigation property.
      // eslint-disable-next-line office-addins/call-sync-before-read
      if (sheet.isNullObject) return;
      sheet.activate();
      sheet.getRange(address).select();
      await ctx.sync();
    });
  } catch (err) {
    console.error(`navigate to ${sheetName}!${address} failed:`, err);
  }
}

/**
 * Delete a named item by name + scope. Throws if the name doesn't exist
 * in the expected collection (so the caller can surface the failure).
 *
 * `scope === "workbook"` deletes from workbook.names; otherwise from
 * worksheets.getItem(scope).names.
 */
async function deleteNamedItem(name: string, scope: string): Promise<void> {
  await Excel.run(async (ctx) => {
    if (scope === "workbook") {
      const item = ctx.workbook.names.getItemOrNullObject(name);
      await ctx.sync();
      // eslint-disable-next-line office-addins/call-sync-before-read
      if (item.isNullObject) {
        throw new Error(`workbook-scoped name "${name}" not found`);
      }
      item.delete();
      await ctx.sync();
      return;
    }
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(scope);
    await ctx.sync();
    // eslint-disable-next-line office-addins/call-sync-before-read
    if (sheet.isNullObject) {
      throw new Error(`sheet "${scope}" not found`);
    }
    const item = sheet.names.getItemOrNullObject(name);
    await ctx.sync();
    // eslint-disable-next-line office-addins/call-sync-before-read
    if (item.isNullObject) {
      throw new Error(`name "${name}" not found on sheet "${scope}"`);
    }
    item.delete();
    await ctx.sync();
  });
}

// ============================================================================
// Audit (review-check) editor dialog
// ============================================================================

interface SaveAuditFromDialog {
  type: "save";
  rule: Omit<UserAuditRule, "id" | "source">;
  existingId?: string;
}

interface CancelAuditFromDialog {
  type: "cancel";
}

type AuditEditorFromDialog = SaveAuditFromDialog | CancelAuditFromDialog | ReadyFromDialog;

interface InitToAuditEditor {
  type: "init";
  rule: AuditRule | null;
}

/**
 * Open the audit-rule (review-check) editor dialog. Resolves with the saved
 * user audit rule, or null if cancelled / closed.
 */
export function openAuditEditor(existing?: AuditRule): Promise<UserAuditRule | null> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/audit-editor.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 65, height: 75, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (rule: UserAuditRule | null): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve(rule);
        };

        const fail = (err: unknown): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as AuditEditorFromDialog;
              if (msg.type === "ready") {
                const init: InitToAuditEditor = { type: "init", rule: existing ?? null };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "save") {
                let saved: UserAuditRule;
                if (msg.existingId) {
                  await updateUserAuditRule(msg.existingId, msg.rule);
                  saved = { id: msg.existingId, source: "user", ...msg.rule };
                } else {
                  saved = await addUserAuditRule(msg.rule);
                }
                finish(saved);
                return;
              }
              if (msg.type === "cancel") {
                finish(null);
                return;
              }
            } catch (err) {
              fail(err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish(null);
        });
      }
    );
  });
}

// ============================================================================
// PDF import dialog
// ============================================================================

interface PdfResultFromDialog {
  type: "result";
  extraction: ExtractionResult;
}

interface PdfCancelFromDialog {
  type: "cancel";
}

interface PdfErrorFromDialog {
  type: "error";
  message: string;
}

/**
 * Fire-and-forget log notification from the dialog: an upload attempt
 * failed but the user is still in the dialog and can retry or cancel.
 * The host fires telemetry + error log without closing the dialog.
 */
interface PdfLogErrorFromDialog {
  type: "log-error";
  message: string;
}

type PdfImportFromDialog =
  | PdfResultFromDialog
  | PdfCancelFromDialog
  | PdfErrorFromDialog
  | PdfLogErrorFromDialog;

/**
 * Open the PDF import dialog. Resolves with the extraction result, or null
 * if the user cancelled / closed the dialog.
 */
export async function openPdfImporter(): Promise<ExtractionResult | null> {
  // The PDF dialog runs in a separate window and can't access the task
  // pane's Clerk instance. Refresh the localStorage token cache here so
  // the dialog can read a fresh JWT when it makes the /api/extract call.
  await getToken();

  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/pdf-import.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 60, height: 65, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (extraction: ExtractionResult | null): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve(extraction);
        };

        const fail = (err: unknown): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          reject(err instanceof Error ? err : new Error(String(err)));
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          try {
            const event = arg as { message: string };
            const msg = JSON.parse(event.message) as PdfImportFromDialog;
            if (msg.type === "result") {
              finish(msg.extraction);
              return;
            }
            if (msg.type === "cancel") {
              finish(null);
              return;
            }
            if (msg.type === "error") {
              fail(new Error(msg.message));
              return;
            }
            if (msg.type === "log-error") {
              // Dialog stays open. Fire telemetry + masked error log so
              // a tester's failed upload still shows up in our dashboards,
              // even if they retry successfully or cancel afterward.
              emitEvent({
                event_name: "PDF_IMPORT_FAILED",
                feature_code: "import-pdf",
                error_code: "PDF_EXTRACT_FAILED",
              });
              reportError({
                error_code: "PDF_EXTRACT_FAILED",
                error: new Error(msg.message),
              });
              return;
            }
          } catch (err) {
            fail(err);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish(null);
        });
      }
    );
  });
}

// ============================================================================
// Data islands dialog
// ============================================================================

interface NavigateFromRegionsDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface CloseFromRegionsDialog {
  type: "close";
}

interface SaveOverridesFromRegionsDialog {
  type: "save-overrides";
  overrides: Record<string, ClassificationRuleOverride>;
}

type RegionsFromDialog =
  | NavigateFromRegionsDialog
  | CloseFromRegionsDialog
  | SaveOverridesFromRegionsDialog
  | ReadyFromDialog;

interface InitToRegions {
  type: "init";
  sheets: SheetRegions[];
  ruleDescriptors: ClassificationRuleDescriptor[];
  ruleOverrides: Record<string, ClassificationRuleOverride>;
  /** True while the secondary classification pass is still running. */
  classifying: boolean;
}

interface UpdateToRegions {
  type: "update";
  sheets: SheetRegions[];
  ruleDescriptors: ClassificationRuleDescriptor[];
  ruleOverrides: Record<string, ClassificationRuleOverride>;
  /** Always false on update (classification has finished). */
  classifying: boolean;
}

interface SaveErrorToRegions {
  type: "save-error";
  message: string;
}

/**
 * Open the data-islands dialog. Stays open until the user closes it; each
 * click on an island (in the mini-map or list) triggers cell navigation.
 * The dialog also hosts the structural-classifier rule editor; saving rule
 * overrides triggers a workbook rescan and pushes new results back.
 */
export function openDataIslands(sheets: SheetRegions[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/regions.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 85, height: 90, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        const handleSaveOverrides = async (
          overrides: Record<string, ClassificationRuleOverride>
        ): Promise<void> => {
          try {
            await saveOverrides(overrides);
            // For a rule-edit rescan, do the full work before responding so
            // the user sees the new classifications in one update (the user
            // explicitly asked to re-classify, not to re-show islands).
            const fastSheets = await scanAllWorksheets();
            const classifiedSheets = await classifyAllRegions(fastSheets);
            const newOverrides = await loadOverrides();
            const update: UpdateToRegions = {
              type: "update",
              sheets: classifiedSheets,
              ruleDescriptors: RULE_DESCRIPTORS,
              ruleOverrides: newOverrides,
              classifying: false,
            };
            dialog.messageChild(JSON.stringify(update));
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            const errMsg: SaveErrorToRegions = { type: "save-error", message };
            try {
              dialog.messageChild(JSON.stringify(errMsg));
            } catch {
              // dialog gone
            }
          }
        };

        const startBackgroundClassification = async (): Promise<void> => {
          try {
            const classifiedSheets = await classifyAllRegions(sheets);
            const update: UpdateToRegions = {
              type: "update",
              sheets: classifiedSheets,
              ruleDescriptors: RULE_DESCRIPTORS,
              ruleOverrides: await loadOverrides(),
              classifying: false,
            };
            try {
              dialog.messageChild(JSON.stringify(update));
            } catch {
              // dialog gone — user closed it before classification finished
            }
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            const errMsg: SaveErrorToRegions = { type: "save-error", message };
            try {
              dialog.messageChild(JSON.stringify(errMsg));
            } catch {
              // dialog gone
            }
          }
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as RegionsFromDialog;
              if (msg.type === "ready") {
                const init: InitToRegions = {
                  type: "init",
                  sheets,
                  ruleDescriptors: RULE_DESCRIPTORS,
                  ruleOverrides: await loadOverrides(),
                  classifying: true,
                };
                dialog.messageChild(JSON.stringify(init));
                // Kick off classification in the background; pushes an
                // `update` message to the dialog when complete.
                void startBackgroundClassification();
                return;
              }
              if (msg.type === "navigate") {
                void navigateToCell(msg.sheetName, msg.address);
                return;
              }
              if (msg.type === "save-overrides") {
                await handleSaveOverrides(msg.overrides);
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              console.error("regions dialog message failed:", err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

// ============================================================================
// Unique formulas dialog
// ============================================================================

interface NavigateFromFormulasDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface CloseFromFormulasDialog {
  type: "close";
}

type FormulasFromDialog =
  | NavigateFromFormulasDialog
  | CloseFromFormulasDialog
  | SaveKindRulesFromFormulasDialog
  | ExportKindRulesFromFormulasDialog
  | ReadyFromDialog;

interface InitToFormulas {
  type: "init";
  regions: FormulaRegion[];
  kindRules: FormulaKindRule[];
}

interface UpdateToFormulas {
  type: "update";
  regions: FormulaRegion[];
  kindRules: FormulaKindRule[];
}

interface SaveErrorToFormulas {
  type: "save-error";
  message: string;
}

interface SaveKindRulesFromFormulasDialog {
  type: "save-kind-rules";
  rules: FormulaKindRule[];
}

interface ExportKindRulesFromFormulasDialog {
  type: "export-kind-rules";
  rules: FormulaKindRule[];
}

/**
 * Open the unique-formulas dialog. Stays open until the user closes it;
 * clicking a row navigates Excel to that formula region.
 */
export function openFormulasReport(regions: FormulaRegion[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/formulas.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 80, height: 85, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as FormulasFromDialog;
              if (msg.type === "ready") {
                const init: InitToFormulas = {
                  type: "init",
                  regions,
                  kindRules: loadFormulaKindRules(),
                };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "save-kind-rules") {
                try {
                  // Empty list = reset to defaults
                  if (msg.rules.length === 0) {
                    resetFormulaKindRules();
                  } else {
                    saveFormulaKindRules(msg.rules);
                  }
                  const newRegions = await scanFormulaRegions();
                  const update: UpdateToFormulas = {
                    type: "update",
                    regions: newRegions,
                    kindRules: loadFormulaKindRules(),
                  };
                  dialog.messageChild(JSON.stringify(update));
                } catch (err) {
                  const message = err instanceof Error ? err.message : String(err);
                  const errMsg: SaveErrorToFormulas = { type: "save-error", message };
                  try {
                    dialog.messageChild(JSON.stringify(errMsg));
                  } catch {
                    // dialog gone
                  }
                }
                return;
              }
              if (msg.type === "export-kind-rules") {
                console.log("[pane] export-kind-rules received; rules =", msg.rules.length);
                try {
                  await exportKindRulesToWorksheet(msg.rules);
                  console.log("[pane] export succeeded");
                  const errMsg: SaveErrorToFormulas = {
                    type: "save-error",
                    message: "Exported. Check the new worksheet.",
                  };
                  // Reuse the save-error channel as a status notice; the dialog
                  // shows whatever message we put here without clobbering the
                  // user's unsaved working rules.
                  dialog.messageChild(JSON.stringify(errMsg));
                } catch (err) {
                  const detail = formatExcelError(err);
                  console.error("[pane] export failed:", detail, err);
                  const errMsg: SaveErrorToFormulas = {
                    type: "save-error",
                    message: `Export failed: ${detail}`,
                  };
                  try {
                    dialog.messageChild(JSON.stringify(errMsg));
                  } catch {
                    // dialog gone
                  }
                }
                return;
              }
              if (msg.type === "navigate") {
                void navigateToCell(msg.sheetName, msg.address);
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              console.error("formulas dialog message failed:", err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

// ============================================================================
// Cell trace dialog
// ============================================================================

interface NavigateFromTraceDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface RefreshFromTraceDialog {
  type: "refresh";
}

interface CloseFromTraceDialog {
  type: "close";
}

type TraceFromDialog =
  | NavigateFromTraceDialog
  | RefreshFromTraceDialog
  | CloseFromTraceDialog
  | ReadyFromDialog;

interface UpdateToTrace {
  type: "update";
  trace: CellTraceData;
}

interface ErrorToTrace {
  type: "error";
  message: string;
}

/**
 * Open the cell trace dialog. Pane fetches the active cell's
 * precedents/dependents and pushes them to the dialog. When the user clicks
 * a row, the dialog asks the pane to navigate, then the pane re-fetches for
 * the new active cell and pushes again — chains indefinitely until the user
 * closes the dialog.
 */
export function openTraceDialog(): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/trace.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 60, height: 70, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        const sendCurrent = async (): Promise<void> => {
          try {
            const trace = await getTraceForActiveCell();
            const update: UpdateToTrace = { type: "update", trace };
            dialog.messageChild(JSON.stringify(update));
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            const errMsg: ErrorToTrace = { type: "error", message: msg };
            try {
              dialog.messageChild(JSON.stringify(errMsg));
            } catch {
              // dialog gone — ignore
            }
          }
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as TraceFromDialog;
              if (msg.type === "ready") {
                await sendCurrent();
                return;
              }
              if (msg.type === "navigate") {
                await navigateToCell(msg.sheetName, msg.address);
                await sendCurrent();
                return;
              }
              if (msg.type === "refresh") {
                await sendCurrent();
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              console.error("trace dialog message failed:", err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

// ============================================================================
// All-strings dialog
// ============================================================================

interface NavigateFromStringsDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface CloseFromStringsDialog {
  type: "close";
}

type StringsFromDialog = NavigateFromStringsDialog | CloseFromStringsDialog | ReadyFromDialog;

interface InitToStrings {
  type: "init";
  strings: StringCell[];
}

/**
 * Open the all-strings dialog. Stays open until the user closes it;
 * clicking a row navigates Excel to that cell.
 */
export function openStringsDialog(strings: StringCell[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/strings.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 80, height: 85, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          try {
            const event = arg as { message: string };
            const msg = JSON.parse(event.message) as StringsFromDialog;
            if (msg.type === "ready") {
              const init: InitToStrings = { type: "init", strings };
              dialog.messageChild(JSON.stringify(init));
              return;
            }
            if (msg.type === "navigate") {
              void navigateToCell(msg.sheetName, msg.address);
              return;
            }
            if (msg.type === "close") {
              finish();
              return;
            }
          } catch (err) {
            console.error("strings dialog message failed:", err);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

// ============================================================================
// Cell types dialog
// ============================================================================

interface SerializedCellTypesConfig {
  weights: { high: number; medium: number; low: number };
  directRules: {
    valueType: boolean;
    numberFormat: boolean;
    textPattern: boolean;
    valueRange: boolean;
  };
  catalog: FunctionCatalogEntry[];
}

interface NavigateFromCellTypesDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface CloseFromCellTypesDialog {
  type: "close";
}

interface SaveConfigFromCellTypesDialog {
  type: "save-config";
  config: SerializedCellTypesConfig;
}

type CellTypesFromDialog =
  | NavigateFromCellTypesDialog
  | CloseFromCellTypesDialog
  | SaveConfigFromCellTypesDialog
  | ReadyFromDialog;

interface InitToCellTypes {
  type: "init";
  reports: CellTypeReport[];
  config: SerializedCellTypesConfig;
  uncatalogued: UncataloguedFunction[];
}

interface UpdateToCellTypes {
  type: "update";
  reports: CellTypeReport[];
  config: SerializedCellTypesConfig;
  uncatalogued: UncataloguedFunction[];
}

interface SaveErrorToCellTypes {
  type: "save-error";
  message: string;
}

function serializeConfig(config: CellTypesConfig): SerializedCellTypesConfig {
  const catalog: FunctionCatalogEntry[] = [];
  for (const [name, spec] of config.catalog) {
    catalog.push({ name, types: spec.types, variadic: !!spec.variadic });
  }
  // Stable order: sort alphabetically for consistent UI display
  catalog.sort((a, b) => a.name.localeCompare(b.name));
  return {
    weights: { ...config.weights },
    directRules: { ...config.directRules },
    catalog,
  };
}

/**
 * Open the cell-types dialog. Stays open until the user closes it; clicking
 * a row navigates Excel to that cell. Reports + config are computed by the
 * pane before the dialog opens. The dialog also supports editing the
 * weights/detectors/catalog in a Rules tab and triggering a rescan; that
 * flow is handled in this opener via the "save-config" message.
 */
export function openCellTypesDialog(
  reports: CellTypeReport[],
  config: CellTypesConfig,
  uncatalogued: UncataloguedFunction[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/cell-types.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 85, height: 90, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        const handleSaveConfig = async (incoming: SerializedCellTypesConfig): Promise<void> => {
          try {
            await saveWeights(incoming.weights);
            await saveDirectRules(incoming.directRules);
            if (incoming.catalog.length === 0) {
              // Empty catalog = "reset to defaults"
              await resetCatalog();
            } else {
              await saveCatalogEntries(incoming.catalog);
            }
            const {
              reports: newReports,
              config: newConfig,
              uncatalogued: newUncatalogued,
            } = await scanCellTypes();
            const update: UpdateToCellTypes = {
              type: "update",
              reports: newReports,
              config: serializeConfig(newConfig),
              uncatalogued: newUncatalogued,
            };
            dialog.messageChild(JSON.stringify(update));
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            const errMsg: SaveErrorToCellTypes = { type: "save-error", message };
            try {
              dialog.messageChild(JSON.stringify(errMsg));
            } catch {
              // dialog gone
            }
          }
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as CellTypesFromDialog;
              if (msg.type === "ready") {
                const init: InitToCellTypes = {
                  type: "init",
                  reports,
                  config: serializeConfig(config),
                  uncatalogued,
                };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "navigate") {
                void navigateToCell(msg.sheetName, msg.address);
                return;
              }
              if (msg.type === "save-config") {
                await handleSaveConfig(msg.config);
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              console.error("cell-types dialog message failed:", err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      }
    );
  });
}

// ============================================================================
// Named ranges dialog
// ============================================================================

interface NavigateFromNamedRangesDialog {
  type: "navigate";
  sheetName: string;
  address: string;
}

interface DeleteFromNamedRangesDialog {
  type: "delete";
  name: string;
  /** "workbook" for workbook-scoped names, sheet name for sheet-scoped. */
  scope: string;
}

interface DeleteBulkFromNamedRangesDialog {
  type: "delete-bulk";
  items: Array<{ name: string; scope: string }>;
}

interface CloseFromNamedRangesDialog {
  type: "close";
}

type NamedRangesFromDialog =
  | NavigateFromNamedRangesDialog
  | DeleteFromNamedRangesDialog
  | DeleteBulkFromNamedRangesDialog
  | CloseFromNamedRangesDialog
  | ReadyFromDialog;

interface InitToNamedRanges {
  type: "init";
  items: NamedRangeInfo[];
}

interface UpdateToNamedRanges {
  type: "update";
  items: NamedRangeInfo[];
}

interface DeleteOkToNamedRanges {
  type: "delete-ok";
  name: string;
}

interface DeleteErrorToNamedRanges {
  type: "delete-error";
  message: string;
}

interface DeleteBulkResultToNamedRanges {
  type: "delete-bulk-result";
  succeeded: number;
  failed: Array<{ name: string; error: string }>;
}

/**
 * Open the named-ranges dialog. Stays open until the user closes it;
 * clicking a row that points to a real range navigates Excel to that
 * range.
 */
export function openNamedRangesDialog(items: NamedRangeInfo[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/named-ranges.html`;
    Office.context.ui.displayDialogAsync(
      url,
      { width: 85, height: 80, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          const message = result.error?.message ?? "unknown error";
          reject(new Error(`displayDialogAsync failed: ${message}`));
          return;
        }

        const dialog = result.value;
        let resolved = false;

        const finish = (): void => {
          if (resolved) return;
          resolved = true;
          try {
            dialog.close();
          } catch {
            // already closed
          }
          resolve();
        };

        const handleDelete = async (name: string, scope: string): Promise<void> => {
          try {
            await deleteNamedItem(name, scope);
            const fresh = await scanNamedRanges();
            items = fresh;
            const okMsg: DeleteOkToNamedRanges = { type: "delete-ok", name };
            try { dialog.messageChild(JSON.stringify(okMsg)); } catch { /* dialog gone */ }
            const update: UpdateToNamedRanges = { type: "update", items: fresh };
            try { dialog.messageChild(JSON.stringify(update)); } catch { /* dialog gone */ }
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            const errMsg: DeleteErrorToNamedRanges = { type: "delete-error", message };
            try { dialog.messageChild(JSON.stringify(errMsg)); } catch { /* dialog gone */ }
          }
        };

        const handleBulkDelete = async (
          batch: Array<{ name: string; scope: string }>,
        ): Promise<void> => {
          const failed: Array<{ name: string; error: string }> = [];
          let succeeded = 0;
          for (const it of batch) {
            try {
              await deleteNamedItem(it.name, it.scope);
              succeeded++;
            } catch (err) {
              failed.push({
                name: it.name,
                error: err instanceof Error ? err.message : String(err),
              });
            }
          }
          const fresh = await scanNamedRanges().catch(() => items);
          items = fresh;
          const result: DeleteBulkResultToNamedRanges = {
            type: "delete-bulk-result",
            succeeded,
            failed,
          };
          try { dialog.messageChild(JSON.stringify(result)); } catch { /* dialog gone */ }
          const update: UpdateToNamedRanges = { type: "update", items: fresh };
          try { dialog.messageChild(JSON.stringify(update)); } catch { /* dialog gone */ }
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          (async () => {
            try {
              const event = arg as { message: string };
              const msg = JSON.parse(event.message) as NamedRangesFromDialog;
              if (msg.type === "ready") {
                const init: InitToNamedRanges = { type: "init", items };
                dialog.messageChild(JSON.stringify(init));
                return;
              }
              if (msg.type === "navigate") {
                void navigateToCell(msg.sheetName, msg.address);
                return;
              }
              if (msg.type === "delete") {
                await handleDelete(msg.name, msg.scope);
                return;
              }
              if (msg.type === "delete-bulk") {
                await handleBulkDelete(msg.items);
                return;
              }
              if (msg.type === "close") {
                finish();
                return;
              }
            } catch (err) {
              console.error("named-ranges dialog message failed:", err);
            }
          })();
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          finish();
        });
      },
    );
  });
}
